import {
  deserify,
  getMediaBasePath,
  IconWrapper,
  IStyles,
  OnlineStatusTag,
  PrimaryButton,
  pxToRem,
  pxTovW,
  SquareImageAvatar,
  StepsBadge,
  theme,
} from '@geneo2-web/shared-ui';
import { Box, Divider, Grid, Typography, useMediaQuery } from '@mui/material';
import { SessionStatusEnum } from '@protos/learning_management/lms.db_pb';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HeaderWithProgressBar } from '../../components/HeaderWithProgressBar';
import { IconDataWithLabel } from '../../components/IconDataWithLabel';
import { useLiveQuizContext } from '../../app/Context/LiveQuizContextProvider';
import { useAppSelector } from '../../reduxStore/reduxHooks';
import { LIVE_QUIZ_PLAY } from '../../routeHandling/RoutesNomenclature';
import { usePauseLiveQuizOnLeave } from './hooks/usePauseLiveQuizOnLeave';
import { LiveQuizStalePopup } from './components/LiveQuizStalePopup';

const styles: IStyles = {
  root: {
    paddingX: { xs: pxToRem(20), md: pxTovW(20), lg: pxTovW(240) },
    paddingBottom: { xs: pxToRem(80), md: pxTovW(40) },
  },
  topBanner: {
    display: 'flex',
    gap: { xs: pxToRem(20), md: pxTovW(7) },
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #007CDC',
    backgroundColor: '#F5FAFF',
    paddingY: { xs: pxToRem(10), md: pxTovW(20), lg: pxTovW(20) },
    marginX: { xs: pxToRem(0), md: pxTovW(20), lg: pxTovW(240) },
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mt: { xs: pxToRem(10), md: pxTovW(15) },
  },
  startButton: {
    width: { xs: '90%', md: pxTovW(500), lg: pxTovW(444) },
    position: { xs: 'fixed', md: 'unset' },
    bottom: { xs: 10, md: 'unset' },
    zIndex: 10,
  },
  bodyBox: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: { xs: 'unset', md: 'space-between' },
    mt: { xs: pxToRem(20), md: pxTovW(40) },
  },
  leftPanel: {
    width: { xs: '100%', md: pxTovW(800), lg: pxTovW(541) },
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(22), md: pxTovW(35) },
  },
  statsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: { xs: pxToRem(10), md: pxTovW(22) },
    boxShadow: `0px 0px ${pxTovW(10)} #00000029`,
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
  },
  studentList: {
    boxShadow: `0px 0px ${pxTovW(10)} #00000029`,
    borderRadius: { xs: pxToRem(15), md: pxTovW(10) },
    paddingX: { xs: pxToRem(14), md: pxTovW(25) },
    maxHeight: { xs: pxToRem(400), md: pxTovW(750), lg: pxTovW(500) },
    overflowY: 'auto',
  },
  studentListHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingY: { xs: pxToRem(14), md: pxTovW(25) },
  },
  rightPanel: {
    width: { xs: '100%', md: pxTovW(900), lg: pxTovW(750) },
    boxSizing: 'border-box',
  },
};

// Same page shape as ClickerAssessmentStart.tsx (top connection banner,
// title row + START button, stats row via the shared IconDataWithLabel,
// bordered scrollable roster panel) rather than a bespoke lookalike — "Live
// Quiz" stands in for "Connected Class" and student rows use SquareImageAvatar's
// `online` dot for "joined" instead of a physical clicker's online status,
// since there's no hardware connection concept here. The right-hand column
// replicates ClickerAssessmentStart's exact "Welcome / Follow these steps"
// heading + numbered StepsBadge instruction cards (same component, same
// colors/layout) instead of the clicker-distribution photos + steps, since
// there's no hardware to distribute here — the three steps below are the
// web-quiz-appropriate equivalent content for the same design.
export const LiveQuizLobby = () => {
  usePauseLiveQuizOnLeave();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { fetchRoster, startLiveQuizQuestion } = useLiveQuizContext();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const { live_quiz_session, question_sessions, roster, joined_participants } =
    deserify(useAppSelector((state) => state.liveQuiz));
  const { current_assessmemt_classStats } = deserify(
    useAppSelector((state) => state.manageHomework)
  );
  useEffect(() => {
    if (live_quiz_session?.liveQuizSessionId && user_info?.teacherProfileId) {
      fetchRoster(
        live_quiz_session.liveQuizSessionId,
        user_info.teacherProfileId
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live_quiz_session?.liveQuizSessionId]);

  // A teacher can end up back on this page after the quiz has already moved
  // past question 1 — browser back button, a stale tab, or simply
  // re-navigating here — since nothing else prevents it. "Start Assessment"
  // below always targets question_sessions[0], so without this redirect,
  // clicking it in that state calls startLiveQuizQuestion for a question
  // that's no longer PENDING; the backend correctly rejects it and the
  // button silently does nothing (reported as "API failing"). Bounce
  // straight to Play whenever a question is already live (currentQuestionSessionId
  // set) *or* question 1 itself is no longer PENDING — covers the brief
  // between-questions window where currentQuestionSessionId is momentarily
  // null (endLiveQuizQuestion clears it before the next one starts) but the
  // quiz has still clearly moved on. Same routing LiveQuizStartTrigger's own
  // resume already uses.
  // useEffect(() => {
  //   const firstQuestionAlreadyStarted =
  //     !!question_sessions[0] &&
  //     question_sessions[0].sessionStatus !== SessionStatusEnum.SESSION_STATUS_UNDEFINED;
  //   if (live_quiz_session?.currentQuestionSessionId || firstQuestionAlreadyStarted) {
  //     navigate(LIVE_QUIZ_PLAY, { replace: true });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [live_quiz_session?.currentQuestionSessionId, question_sessions]);

  const totalStudents = roster?.length ?? joined_participants.length;
  // const totalJoined = joined_participants.length;

  // roster[].hasJoined is a one-time snapshot from the fetchRoster call
  // above — it never updates as students join afterward. joined_participants
  // is the live-updating source (pushed via the Firestore session-doc
  // listener), so each row's actual status has to be derived from that, not
  // trusted off the roster entry itself — matching how totalJoined above
  // already does. Sorted joined-first (alphabetical), then not-yet-joined
  // (alphabetical), recomputed whenever either list changes.
  const joinedIds = useMemo(
    () =>
      new Set(
        joined_participants
          .filter((p) => p.isOnline)
          .map((p) => String(p.studentId))
      ),
    [joined_participants]
  );
  const totalJoined = joinedIds.size;

  const sortedRoster = useMemo(() => {
    return [...(roster ?? [])].sort((a, b) => {
      const aJoined = joinedIds.has(String(a.studentId));
      const bJoined = joinedIds.has(String(b.studentId));
      if (aJoined !== bJoined) return aJoined ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [roster, joinedIds]);

  const handleStartFirstQuestion = async () => {
    console.log('handleStartFirstQuestion', {
      live_quiz_session,
      question_sessions,
      user_info,
    });
    if (
      !live_quiz_session ||
      !user_info?.teacherProfileId ||
      !question_sessions.length
    )
      return;
    const firstQuestion = question_sessions[0];
    const res = await startLiveQuizQuestion(
      live_quiz_session.liveQuizSessionId,
      firstQuestion.liveQuizQuestionSessionId,
      user_info.teacherProfileId
    );
    if (res) navigate(LIVE_QUIZ_PLAY);
  };

  const subjectSectionLabel = [
    live_quiz_session?.className,
    live_quiz_session?.sectionName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box>
      <Box sx={styles.topBanner}>
        <IconWrapper
          name="wifi"
          size={isMobile ? 'large' : 'md'}
          parentFolder="icons"
          type="png"
        />
        <Typography variant="h2">Live Quiz</Typography>
      </Box>
      <Box sx={styles.root}>
        <Box sx={styles.headerContainer}>
          <Box
            sx={{ width: { xs: '100%', md: pxTovW(1000), lg: pxTovW(602) } }}
          >
            <HeaderWithProgressBar
              imagePath={getMediaBasePath(
                current_assessmemt_classStats?.assessment
                  ?.assessmentPosterImgUrl,
                'processedMediaBucket'
              )}
              primaryTitle={live_quiz_session?.taskTitle || 'Live Quiz'}
              secondaryTitle={
                [live_quiz_session?.subject, subjectSectionLabel]
                  .filter(Boolean)
                  .join(' | ') || undefined
              }
            />
          </Box>

          <PrimaryButton
            onClick={handleStartFirstQuestion}
            sx={styles.startButton}
            disabled={!question_sessions.length}
            color="secondary"
          >
            <Typography variant="h3" color="common.white" fontWeight="bold">
              Start Assessment
            </Typography>
          </PrimaryButton>
        </Box>

        <Box sx={styles.bodyBox}>
          <Box sx={styles.leftPanel}>
            <Box sx={styles.statsRow}>
              <IconDataWithLabel
                iconName="user-plus"
                data={`${totalJoined}/${totalStudents}`}
                label="Joined"
              />
              <IconDataWithLabel
                iconName="user2"
                data={totalStudents.toString()}
                label="Total"
              />
              <IconDataWithLabel
                iconName="science2"
                data={subjectSectionLabel || ' '}
                label={live_quiz_session?.subject || ' '}
                hideDivider
              />
            </Box>

            <Box sx={styles.studentList}>
              <Box sx={styles.studentListHeader}>
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: pxToRem(18), md: pxTovW(24) } }}
                >
                  Name
                </Typography>
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: pxToRem(18), md: pxTovW(24) } }}
                >
                  Status
                </Typography>
              </Box>

              <Divider orientation="horizontal" flexItem />

              <Box>
                {sortedRoster.map((student, index) => {
                  const hasJoined = joinedIds.has(String(student.studentId));
                  return (
                    <Box key={String(student.studentId)}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingY: { xs: pxToRem(14), md: pxTovW(25) },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: pxToRem(10), md: pxTovW(25) },
                          }}
                        >
                          <SquareImageAvatar
                            size="small"
                            name={student.name}
                            ImageSource={student.profilePicUrl}
                            online={hasJoined}
                          />
                          <Typography
                            variant="h2"
                            fontWeight="regular"
                            sx={{
                              fontSize: { xs: pxToRem(16), md: pxTovW(24) },
                              textTransform: 'capitalize',
                            }}
                          >
                            {student.name}
                          </Typography>
                        </Box>
                        <OnlineStatusTag
                          status={hasJoined ? 'online' : 'offline'}
                        />
                      </Box>
                      {index < sortedRoster.length - 1 && (
                        <Divider orientation="horizontal" flexItem />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box sx={styles.rightPanel}>
            {!isMobile && (
              <>
                <Box>
                  <Typography
                    variant="h1"
                    textAlign="center"
                    sx={{ fontSize: { xs: pxToRem(14), md: pxTovW(40) } }}
                  >
                    Welcome To Your Live Quiz.
                  </Typography>
                  <Typography
                    variant="h4"
                    textAlign="center"
                    sx={{
                      fontSize: { xs: pxToRem(12), md: pxTovW(32) },
                      color: '#666666',
                      mt: pxToRem(10),
                    }}
                  >
                    Follow these steps for the best experience.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    mt: { xs: pxToRem(25), md: pxTovW(40) },
                    gap: { xs: pxToRem(20), md: pxTovW(30) },
                  }}
                >
                  {LIVE_QUIZ_STEPS_DATA.map((info, index) => (
                    <Grid
                      key={index}
                      container
                      sx={{
                        display: 'flex',
                        justifyContent: { xs: 'center', md: 'space-between' },
                        alignItems: 'center',
                        gap: { xs: pxToRem(10), md: pxTovW(15) },
                      }}
                    >
                      <Grid item xs={12} md={2.2}>
                        <StepsBadge
                          number={index + 1}
                          backgroundColor={info.countBackground}
                          bordercolor={info.countBorderColor}
                          rootStyles={{
                            width: { xs: pxToRem(90), md: pxTovW(124) },
                            margin: { xs: 'auto', md: 'unset' },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={9.5}>
                        <Typography
                          variant="h3"
                          fontWeight="bold"
                          sx={{
                            textAlign: { xs: 'center', md: 'unset' },
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            fontSize: { xs: pxToRem(14), md: pxTovW(22) },
                          }}
                        >
                          {info.stepInstructions}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
      <LiveQuizStalePopup />
    </Box>
  );
};

// Same shape as ClickerAssessmentStart.tsx's STEPS_DATA (badge colors match
// exactly), content swapped for what's actually true of a web live quiz
// (browser-based join, no clicker hardware to distribute).
const LIVE_QUIZ_STEPS_DATA = [
  {
    countBackground: '#FFF4CA',
    countBorderColor: '#F8C807',
    stepInstructions:
      'Students see a "Join Now" prompt on Home the moment you start - no code needed.',
  },
  {
    countBackground: '#E8F5FF',
    countBorderColor: '#007CDC',
    stepInstructions:
      "Watch the join count above and start whenever you're ready - latecomers can still join mid-quiz.",
  },
  {
    countBackground: '#FFE4E4',
    countBorderColor: '#F54040',
    stepInstructions:
      'Answers stay hidden from everyone, including you, until you end each question.',
  },
];

export default LiveQuizLobby;
